// src/contexts/AuthContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { 
  User,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from 'firebase/auth';
import { auth } from '../config/firebase';
import Cookies from 'js-cookie';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenRefreshed, setTokenRefreshed] = useState(false);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        // Only get new token if we don't have one or it's expired
        const existingToken = Cookies.get('authToken');
        
        if (!existingToken || !tokenRefreshed) {
          console.log('Getting fresh token from Firebase');
          const token = await firebaseUser.getIdToken();
          Cookies.set('authToken', token, { expires: 7 });
          Cookies.set('user', JSON.stringify({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
          }), { expires: 7 });
          setTokenRefreshed(true);
        } else {
          console.log('Using existing token from cookies');
        }
        
        setUser(firebaseUser);
      } else {
        // Clear cookies on logout
        Cookies.remove('authToken');
        Cookies.remove('user');
        setUser(null);
        setTokenRefreshed(false);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, [tokenRefreshed]);

  const login = async (email: string, password: string) => {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const token = await userCredential.user.getIdToken();
    
    // Store token and user info in cookies
    Cookies.set('authToken', token, { expires: 7 });
    Cookies.set('user', JSON.stringify({
      uid: userCredential.user.uid,
      email: userCredential.user.email,
      displayName: userCredential.user.displayName,
    }), { expires: 7 });
  };

  const logout = async () => {
    await signOut(auth);
    Cookies.remove('authToken');
    Cookies.remove('user');
  };

  const value = {
    user,
    loading,
    login,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};